import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from "react";

export const inputClass =
  "h-12 w-full rounded-md border bg-white px-4 text-base text-gray-900 placeholder:text-gray-500 " +
  "transition-colors outline-none focus:border-primary-900 disabled:bg-gray-300 disabled:text-gray-600";

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  suffix?: ReactNode;
}

/** 라벨·도움말·오류를 갖춘 입력. 오류가 있으면 테두리와 문구를 negative 로 바꾼다. */
export const TextField = forwardRef<HTMLInputElement, FieldProps>(function TextField(
  { label, hint, error, suffix, className = "", id, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      {label && (
        <label htmlFor={inputId} className="text-sm font-semibold text-gray-700">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          ref={ref}
          id={inputId}
          aria-invalid={!!error}
          aria-describedby={error || hint ? `${inputId}-desc` : undefined}
          className={`${inputClass} ${error ? "border-negative" : "border-gray-400"} ${suffix ? "pr-24" : ""}`}
          {...rest}
        />
        {suffix && <div className="absolute inset-y-0 right-2 flex items-center">{suffix}</div>}
      </div>
      {(error || hint) && (
        <p id={`${inputId}-desc`} className={`text-xs ${error ? "text-negative" : "text-gray-600"}`}>
          {error || hint}
        </p>
      )}
    </div>
  );
});

export function FieldLabel({ children, optional }: { children: ReactNode; optional?: boolean }) {
  return (
    <span className="text-sm font-semibold text-gray-700">
      {children}
      {optional && <span className="ml-1 font-normal text-gray-500">(선택)</span>}
    </span>
  );
}
